import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type { DoctorAvailabilityGroup } from 'features/Schedule/api/schedule.types'

import { awayStaffLabel } from './awayOverlap'
import type { ResourceAway } from './resourceAwayStore'
import { buildDisplayBlocks } from './scheduleAvailability'

type Resource = { resourceId: string; roomResourceId?: string; staffId?: number }

type Args = {
  resources: Resource[]
  rooms: IPatientRoom[]
  resourceGrouping: 'rooms' | 'staff'
  availability: DoctorAvailabilityGroup[]
  availabilityStaffIds: number[]
  emrLinkedUserIds: Set<number>
}

// Whose availability each column paints: its doctor in staff columns, the waiting room's in room view.
const staffIdsFor = (
  resource: Resource,
  { rooms, resourceGrouping, availabilityStaffIds, emrLinkedUserIds }: Args,
): number[] => {
  if (resourceGrouping === 'staff') {
    const staffId = Number(resource.resourceId)
    return Number.isFinite(staffId) && staffId > 0 && emrLinkedUserIds.has(staffId) ? [staffId] : []
  }

  const room = rooms.find(
    (item) => `${item.label}/${item.value}` === (resource.roomResourceId ?? resource.resourceId),
  )
  if (resourceGrouping !== 'rooms' || !room?.is_waiting_room || resource.staffId === 0) return []
  return (resource.staffId != null ? [resource.staffId] : availabilityStaffIds).filter((staffId) =>
    emrLinkedUserIds.has(staffId),
  )
}

export const buildResourceAway = (args: Args): Map<string, ResourceAway> => {
  const byResource = new Map<string, ResourceAway>()
  args.resources.forEach((resource) => {
    const staffIds = staffIdsFor(resource, args)
    if (!staffIds.length) return
    byResource.set(String(resource.resourceId), {
      blocks: buildDisplayBlocks(args.availability, staffIds),
      staffLabel: awayStaffLabel(args.availability, staffIds),
    })
  })
  return byResource
}

// Week view columns are days and only one doctor's availability is shown, keyed by date.
export const buildWeekAway = (
  availabilityByDate: Map<string, DoctorAvailabilityGroup[]>,
  staffId: number | undefined,
): Map<string, ResourceAway> => {
  const byDate = new Map<string, ResourceAway>()
  if (staffId == null) return byDate
  // A day off carries no name, so the label comes from the whole week.
  const staffLabel = awayStaffLabel([...availabilityByDate.values()].flat(), [staffId])
  availabilityByDate.forEach((groups, date) => {
    byDate.set(date, { blocks: buildDisplayBlocks(groups, [staffId]), staffLabel })
  })
  return byDate
}
